<template>
    <v-layout :class="$options.name" justify-center>
        <v-flex xs12 sm10 md8>
            <v-card>
                <v-card-title class="secondary elevation-4 white--text">
                    <div class="text-h5">
                        <v-icon medium color="white"> fa-tasks </v-icon>
                        {{ $t('Alert_Information') }}
                    </div>
                </v-card-title>
                <v-progress-linear v-show="state.show.isProcessing" color="accent" indeterminate />
                <v-card-text>
                    <alert-error
                        v-model="state.update.error"
                        :title="state.update.errorTitle"
                        :details="state.update.errorDetails"
                        class="mr-2 ml-2"
                    />
                    <v-form ref="form" v-model="isValid" @submit.prevent>
                        <v-container grid-list-md class="pa-0">
                            <v-layout wrap>
                                <v-flex xs4 class="text-body-2 required">
                                    <label>{{ $t('Alert_Display_Period') }}</label>
                                </v-flex>
                                <v-flex xs8>
                                    <text-field-label
                                        v-model="hours"
                                        class-name="pt-0"
                                        :rules="[rules.required, rules.decimalIntegerRange(hours, 1, 10000)]"
                                        :disabled="isTextFieldDisabled"
                                        :suffix="$t('Hours')"
                                        :editable="$can('update', 'SystemSetting')"
                                        @keydown.13="updateSettingIfCanPress"
                                    />
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                    <v-layout wrap>
                        <small class="red--text" text-left>
                            <span class="required-before"> {{ $t('Indicates_required_field') }} </span>
                        </small>
                    </v-layout>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <button-app-control
                        v-can:update="['SystemSetting']"
                        :disabled="isUpdateButtonDisabled"
                        :loading="state.update.isProcessing"
                        :label="$t('Update')"
                        icon="refresh"
                        @click="updateSetting"
                    />
                </v-card-actions>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script lang="ts">
import { mixins } from 'vue-class-component';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import type { SettingState, SystemSetting } from '../../../store/modules/setting/types';
import { AlertError, ButtonAppControl, TextFieldLabel } from '../../atoms';
import InputValidation from '../../../mixins/InputValidation';

@Component({
    name: 'TheAlertSettingInformation',
    components: {
        AlertError,
        ButtonAppControl,
        TextFieldLabel,
    },
})
export default class TheAlertSettingInformation extends mixins(InputValidation) {
    @State('setting') state: SettingState;
    @Action('show', { namespace: 'setting' }) show;
    @Action('update', { namespace: 'setting' }) update;
    @Prop({ default: false }) readonly otherProcessing: boolean;

    @Watch('isCreated', { immediate: true })
    onIsCreatedChanged(isCreated: boolean): void {
        if (isCreated) {
            this.$nextTick((this as any).$refs.form.resetValidation);
        }
    }
    isCreated = false;

    hours: number = 120;

    /**
     * lifecycle hooks
     */
    async created() {
        await this.fetchData();
        this.isCreated = true;
    }

    /**
     * getter
     */
    get isEdited(): boolean {
        if (!this.state.systemSetting || !this.state.systemSetting.alert) {
            return false;
        }
        return this.hours !== this.state.systemSetting.alert.period_in_hours;
    }
    get isTextFieldDisabled(): boolean {
        return this.state.show.isProcessing || this.state.update.isProcessing || this.otherProcessing;
    }
    get isUpdateButtonDisabled(): boolean {
        return !(this as any).isValid || !this.isEdited || this.state.show.isProcessing || this.otherProcessing;
    }

    /**
     * methods
     */
    async fetchData() {
        await this.show('alert');
        if (!this.state.show.error) {
            this.hours = this.state.systemSetting.alert.period_in_hours;
        }
        window.scrollTo(0, 0);
    }
    async updateSetting() {
        if (!this.isEdited) {
            return;
        }
        const params: SystemSetting = {
            alert: {
                period_in_hours: this.hours,
            },
        };
        await this.update(params);
        if (!this.state.update.error) {
            this.fetchData();
        }
    }
    async updateSettingIfCanPress() {
        if (this.isUpdateButtonDisabled || this.state.update.isProcessing) return;
        await this.updateSetting();
    }
}
</script>
